import { useCallback, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
    Badge,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledDropdown,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faEllipsisV,
    faArrowCircleRight,
    faPlayCircle,
    faPauseCircle,
    faQuestion,
} from '@fortawesome/free-solid-svg-icons';
import PluginNotesIcon from '..//Icons/PluginNotesIcon';
import PluginRemoveIcon from '../Icons/PluginRemoveIcon';
import PluginClientCommandConfirmationModal from '../../../../../../Modals/MissionControl/ClientManifest/PluginClientCommandConfirmationModal';
import StatusWithTooltip from '../ClientStatus/StatusWithTooltip';
import hasPermission from '../../../../../../Shared/hasPermission';

const ClientPluginsBody = ({
    instanceId,
    plugins,
    handlePluginEdit,
    handlepluginNotes,
    handleRemovePlugin,
    canEditPlugins,
    statuses,
}) => {
    const [confirmCommandModalOpen, setConfirmCommandModalOpen] =
        useState(false);

    //Command Type State to change Plugin State
    const [currentPluginId, setCurrentPluginId] = useState('');
    const [commandType, setCommandType] = useState('');

    const permissions = useSelector(
        (state) => state.auth.permissions,
        shallowEqual
    );

    //Permissions
    const canRemovePlugins = hasPermission(
        ['ClientPluginsRemove'],
        permissions
    );

    const toggleConfirmCommand = useCallback(
        () => setConfirmCommandModalOpen(!confirmCommandModalOpen),
        [confirmCommandModalOpen]
    );

    function getKey(uid, version) {
        return uid + version;
    }

    function findStatus(id, version, statuses) {
        return statuses.find(
            (status) => status.id === id && status.version === version
        );
    }

    function intersection(setA, setB, equals) {
        return new Set(
            [...setA].filter((a) => Array.from(setB).some((b) => equals(a, b)))
        );
    }

    function difference(setA, setB, equals) {
        return new Set(
            [...setA].filter((a) => !Array.from(setB).some((b) => equals(a, b)))
        );
    }

    const IntersectionComponent = () => {
        const intersectedSet = intersection(
            plugins,
            statuses,
            (x, y) => x.uid === y.id && x.version === y.version
        );
        return (
            <>
                {Array.from(intersectedSet).map((item, index) =>
                    getFullPluginRow(
                        item,
                        findStatus(item.uid, item.version, statuses)
                    )
                )}
            </>
        );
    };

    const ManifestPluginsExcludingPluginStatusesComponent = () => {
        const differenceSet = difference(
            plugins,
            statuses,
            (x, y) => x.uid === y.id && x.version === y.version
        );
        return (
            <>
                {Array.from(differenceSet).map((item, index) =>
                    getPluginRow(item)
                )}
            </>
        );
    };

    const PluginStatusesExcludingManifestPluginsComponent = () => {
        const differenceSet = difference(
            statuses,
            plugins,
            (x, y) => x.id === y.uid && x.version === y.version
        );
        return (
            <>
                {Array.from(differenceSet).map((item, index) =>
                    getStatusRow(item)
                )}
            </>
        );
    };

    const getRunningIcon = (status) => {
        if (status?.state?.running === 3) {
            return (
                <FontAwesomeIcon
                    icon={faPlayCircle}
                    sm={'true'}
                    title="Plugin is running"
                    className="glowingIcon"
                />
            );
        } else if (status?.state?.running === 2) {
            return (
                <FontAwesomeIcon
                    icon={faPauseCircle}
                    sm={'true'}
                    title="Plugin is stopped"
                />
            );
        } else {
            return (
                <FontAwesomeIcon
                    icon={faQuestion}
                    sm={'true'}
                    title="Cannot determine if the plugin is running"
                />
            );
        }
    };

    // if all we have is a status but no plugin
    const getStatusRow = (status) => {
        return (
            <tr key={hashCode(getKey(status.id, status.version))}>
                <td></td>
                <td>
                    <span>{status.name}</span>
                    <br />
                    <small>{status.id}</small>
                </td>
                <td style={{ verticalAlign: 'middle' }}>{status.version}</td>
                <td>
                    <div className="statustooltip">
                        <h6>
                            <Badge color="danger">
                                On machine but not on manifest
                            </Badge>
                        </h6>
                    </div>
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                    {getRunningIcon(status)}
                </td>
                <td></td>
                <td></td>
            </tr>
        );
    };

    const getPluginRow = (plugin) => {
        return (
            <tr
                key={hashCode(getKey(plugin.uid, plugin.version))}
                className={getRowClass(plugin.isInherited)}>
                <td></td>
                <td>
                    <span>{plugin.name}</span>
                    <br />
                    <small>{plugin.uid}</small>
                </td>
                <td style={{ verticalAlign: 'middle' }}>{plugin.version}</td>
                <td>
                    <div className="statustooltip">
                        <h6>
                            <Badge color="danger">
                                On manifest but not machine
                            </Badge>
                        </h6>
                    </div>
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                    {getRunningIcon(null)}
                </td>
                <td style={{ verticalAlign: 'middle' }} className="text-center">
                    {canEditPlugins && (
                        <FontAwesomeIcon
                            icon={faEdit}
                            className="options-icon"
                            onClick={() => handlePluginEdit(plugin)}
                        />
                    )}
                    <PluginNotesIcon
                        noteText={plugin.notes}
                        onClickBehavior={handlepluginNotes}
                    />
                    {canRemovePlugins && (
                        <PluginRemoveIcon
                            plugin={plugin}
                            onClickBehavior={handleRemovePlugin}
                        />
                    )}
                </td>
                <td></td>
            </tr>
        );
    };

    // we have a plugin and a status
    const getFullPluginRow = (plugin, status) => {
        return (
            <tr
                key={hashCode(getKey(plugin.uid, plugin.version))}
                className={getRowClass(plugin.isInherited)}>
                <td style={{ verticalAlign: 'middle' }}>
                    {plugin.isInherited && (
                        <FontAwesomeIcon
                            icon={faArrowCircleRight}
                            sm={'true'}
                            title="Plugin Inherited"
                        />
                    )}
                </td>
                <td>
                    {plugin.name}
                    <br />
                    <small>{plugin.uid}</small>
                </td>
                <td style={{ verticalAlign: 'middle' }}>{plugin.version}</td>
                <td>
                    <StatusWithTooltip
                        id={hashCode(getKey(plugin.uid, plugin.version))}
                        currentStatus={status} //TODO: Get actual status
                        targetStatus={plugin.status}
                    />
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                    {getRunningIcon(status)}
                </td>
                <td style={{ verticalAlign: 'middle' }} className="text-center">
                    {canEditPlugins && (
                        <FontAwesomeIcon
                            icon={faEdit}
                            className="options-icon"
                            onClick={() => handlePluginEdit(plugin)}
                        />
                    )}
                    <PluginNotesIcon
                        noteText={plugin.notes}
                        onClickBehavior={handlepluginNotes}
                    />
                    {canRemovePlugins && (
                        <PluginRemoveIcon
                            plugin={plugin}
                            onClickBehavior={handleRemovePlugin}
                        />
                    )}
                </td>
                <td>
                    {canEditPlugins && (
                        <UncontrolledDropdown>
                            <DropdownToggle
                                nav
                                className="text-light"
                                style={{
                                    position: 'absolute',
                                    right: '0px',
                                }}>
                                <FontAwesomeIcon icon={faEllipsisV} size="lg" />
                            </DropdownToggle>
                            <DropdownMenu right>
                                <DropdownItem
                                    onClick={() =>
                                        triggerCommand(
                                            plugin.uid,
                                            setCurrentPluginId,
                                            'Restart',
                                            setCommandType,
                                            toggleConfirmCommand
                                        )
                                    }>
                                    {'Restart'}
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        triggerCommand(
                                            plugin.uid,
                                            setCurrentPluginId,
                                            'Reinstall',
                                            setCommandType,
                                            toggleConfirmCommand
                                        )
                                    }>
                                    {'Reinstall'}
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    )}
                </td>
            </tr>
        );
    };

    return (
        <>
            <PluginClientCommandConfirmationModal
                instanceId={instanceId}
                pluginId={currentPluginId}
                type={commandType}
                toggle={toggleConfirmCommand}
                isOpen={confirmCommandModalOpen}
            />
            <tbody>
                {ManifestPluginsExcludingPluginStatusesComponent()}
                {IntersectionComponent()}
                {PluginStatusesExcludingManifestPluginsComponent()}
            </tbody>
        </>
    );
};

// Determine plugin row colors based on if plugin is inherited
const inherit = `bg-tr-inherited font-weight-bold`;

const getRowClass = (IsInherit) => (IsInherit ? inherit : '');

const triggerCommand = (
    pluginId,
    setCurrentPluginId,
    type,
    setCommandType,
    toggle
) => {
    setCurrentPluginId(pluginId);
    setCommandType(type);
    toggle();
};

function hashCode(s) {
    return s.split('').reduce(function (a, b) {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
    }, 0);
}

export default ClientPluginsBody;
